/**
 * Field formatters and display labels for the POD claim change-history panel.
 * Mirrors the labels used in `PodClaimPanel.tsx` so the before/after diff
 * reads the same as the form the practitioner edited.
 *
 * Used by: `PodClaimPanel.tsx` -> `<ChangeHistory />`.
 */

import {
  POD_CREDITOR_TYPE_LABELS,
  POD_RECOMMENDATION_LABELS,
  POD_STATUS_LABELS,
  POD_SUPPORTING_DOC_LABELS,
  fmtDate,
  fmtMoney,
} from "./podShared";

const EM_DASH = "\u2014";

const money = (v: unknown): string =>
  typeof v === "number" ? fmtMoney(v) : EM_DASH;

const yesNo = (v: unknown): string =>
  v === undefined || v === null ? EM_DASH : v ? "Yes" : "No";

const label =
  (labels: Record<string, string>) =>
  (v: unknown): string =>
    labels[String(v)] ?? String(v ?? EM_DASH);

/** Supporting doc types are stored as an array of enum keys. */
const docTypes = (v: unknown): string => {
  if (!Array.isArray(v) || v.length === 0) return EM_DASH;
  const labels: Record<string, string> = POD_SUPPORTING_DOC_LABELS;
  return v.map((k) => labels[String(k)] ?? String(k)).join(", ");
};

export const POD_CLAIM_FIELD_FORMATTERS: Record<
  string,
  (value: unknown) => string
> = {
  claimedNet: money,
  claimedGst: money,
  claimedTotal: money,
  securityHeldValue: money,
  substantiatedTotal: money,
  admittedAmount: money,
  rejectedAmount: money,
  podDate: (v) => (typeof v === "string" && v ? fmtDate(v) : EM_DASH),
  status: label(POD_STATUS_LABELS),
  creditorType: label(POD_CREDITOR_TYPE_LABELS),
  recommendation: label(POD_RECOMMENDATION_LABELS),
  supportingDocTypes: docTypes,
  podExecuted: yesNo,
  correctlyWitnessed: yesNo,
  correctlyCompleted: yesNo,
  // Line-item fields (added / edited / deleted claim and supporting lines).
  amount: money,
  date: (v) => (typeof v === "string" && v ? fmtDate(v) : EM_DASH),
};

export const POD_CLAIM_FIELD_LABELS: Record<string, string> = {
  creditorName: "Creditor",
  creditorReference: "Creditor reference",
  creditorType: "Creditor type",
  status: "Status",
  podDate: "POD date",
  claimedNet: "Claimed (net)",
  claimedGst: "Claimed GST",
  claimedTotal: "Claimed total",
  securityHeldValue: "Security held (value)",
  securityHeldDescription: "Security held (particulars)",
  substantiatedTotal: "Substantiated total",
  supportingDocTypes: "Supporting documents",
  supportingDocOther: "Supporting documents (other)",
  podExecuted: "POD executed",
  correctlyWitnessed: "Correctly witnessed",
  correctlyCompleted: "Correctly completed",
  recommendation: "Recommendation",
  admittedAmount: "Admitted",
  rejectedAmount: "Rejected",
  groundsForRejection: "Grounds for rejection",
  notes: "Notes",
  // Line-item fields.
  description: "Description",
  amount: "Amount",
  date: "Date",
  reference: "Reference",
};
